import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { epicengageSupabase } from '../integrations/supabase/client';
import { useAuth } from './useEngageAuth';
import type { Merchant, MerchantSettings, SequenceKey, IndustryType, ToneType } from '../types/epicengage';

interface MerchantContextType {
  merchant: Merchant | null;
  settings: MerchantSettings | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createMerchant: (data: { name: string; industry: Merchant['industry']; region: Merchant['region'] }) => Promise<Merchant>;
  updateMerchant: (data: { name?: string; industry?: Merchant['industry']; region?: Merchant['region'] }) => Promise<void>;
  updateSettings: (settings: Partial<MerchantSettings>) => Promise<void>;
}

const MerchantContext = createContext<MerchantContextType | undefined>(undefined);

// Template content generators based on industry and business name
const generateTemplateContent = (
  templateKey: string,
  businessName: string,
  industry: IndustryType,
  tone: ToneType = 'FRIENDLY'
) => {
  const industryContext: Record<IndustryType, { noun: string; action: string; cta: string }> = {
    FNB: { noun: 'delicious meals', action: 'dine with us', cta: 'View Menu' },
    RETAIL: { noun: 'amazing products', action: 'shop with us', cta: 'Shop Now' },
    HEALTHCARE: { noun: 'quality care', action: 'visit us', cta: 'Book Appointment' },
    WELLNESS: { noun: 'wellness services', action: 'experience relaxation', cta: 'Book Session' }
  };

  const ctx = industryContext[industry];

  const templates: Record<string, { subject: string; body: string }> = {
    // New Customer Onboarding
    onboarding_welcome: {
      subject: `Welcome to ${businessName}! 🎉`,
      body: `Hi {{customer.first_name}},\n\nWelcome to ${businessName}! We're thrilled to have you join our community.\n\nAt ${businessName}, we're passionate about bringing you ${ctx.noun}. Whether you're here for the first time or returning, we promise to make every experience memorable.\n\nLooking forward to seeing you soon!\n\nWarm regards,\nThe ${businessName} Team`
    },
    onboarding_perks: {
      subject: `Your exclusive perks at ${businessName} 🎁`,
      body: `Hi {{customer.first_name}},\n\nAs a valued member of our community, we wanted to share some perks that await you:\n\n✨ Early access to new ${ctx.noun}\n✨ Exclusive member-only offers\n✨ Special birthday treats\n\nDon't miss out on these benefits – ${ctx.action} again soon!\n\nCheers,\nThe ${businessName} Team`
    },
    onboarding_return: {
      subject: `We'd love to see you again at ${businessName}`,
      body: `Hi {{customer.first_name}},\n\nIt's been a few days since you joined ${businessName}, and we wanted to check in.\n\nHave questions? We're here to help! Just reply to this email.\n\nReady to ${ctx.action}? We can't wait to welcome you back.\n\n${ctx.cta} →\n\nSee you soon,\nThe ${businessName} Team`
    },
    // Post-Action Follow-up
    post_action_thanks: {
      subject: `Thank you for choosing ${businessName}! ❤️`,
      body: `Hi {{customer.first_name}},\n\nThank you so much for your recent visit to ${businessName}!\n\nWe truly appreciate your support and hope you enjoyed your experience. Your satisfaction means everything to us.\n\nIf you have a moment, we'd love to hear your feedback – it helps us improve and serve you better.\n\nSee you again soon!\n\nWith gratitude,\nThe ${businessName} Team`
    },
    // Re-engagement (30 days)
    reengagement_30d: {
      subject: `We miss you at ${businessName}! 💫`,
      body: `Hi {{customer.first_name}},\n\nIt's been a while since your last visit to ${businessName}, and we've been thinking about you!\n\nWe've got some exciting ${ctx.noun} waiting for you, and we'd love for you to come back and experience them.\n\nAs a special welcome back, we'd love to offer you something special on your next visit. Just reply to this email to learn more!\n\nHope to see you soon,\nThe ${businessName} Team`
    }
  };

  return templates[templateKey] || { subject: 'Hello from ' + businessName, body: 'Thank you for being a customer!' };
};

// Automation template definitions
const AUTOMATION_TEMPLATES: Record<SequenceKey, { templates: { key: string; name: string; delay_hours: number }[] }> = {
  NEW_CUSTOMER_ONBOARDING: {
    templates: [
      { key: 'onboarding_welcome', name: 'Welcome Email', delay_hours: 0 },
      { key: 'onboarding_perks', name: 'Perks & Benefits', delay_hours: 48 },
      { key: 'onboarding_return', name: 'Return Reminder', delay_hours: 120 }
    ]
  },
  POST_ACTION_FOLLOWUP: {
    templates: [
      { key: 'post_action_thanks', name: 'Thank You', delay_hours: 0 }
    ]
  },
  INACTIVE_30D: {
    templates: [
      { key: 'reengagement_30d', name: 'We Miss You', delay_hours: 0 }
    ]
  }
};

export function MerchantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [settings, setSettings] = useState<MerchantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMerchant = async () => {
    if (!user) {
      setMerchant(null);
      setSettings(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Fetch merchant (use limit 1 + order to handle potential duplicates gracefully)
      const { data: merchantRows, error: merchantError } = await epicengageSupabase
        .from('merchants')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1);

      if (merchantError) throw merchantError;
      const merchantData = merchantRows?.[0] ?? null;

      if (merchantData) {
        setMerchant(merchantData as Merchant);

        // Fetch settings
        const { data: settingsData, error: settingsError } = await epicengageSupabase
          .from('merchant_settings')
          .select('*')
          .eq('merchant_id', merchantData.id)
          .maybeSingle();

        if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;
        setSettings(settingsData as MerchantSettings | null);
      } else {
        // Auto-create merchant with defaults for new users
        await autoCreateMerchant();
      }
    } catch (err: any) {
      console.error('Error fetching merchant:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-create merchant with defaults when user first logs in
  const autoCreateMerchant = async () => {
    if (!user) return;

    try {
      // Double-check no merchant was created in the meantime (race condition guard)
      const { data: existingCheck } = await epicengageSupabase
        .from('merchants')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (existingCheck && existingCheck.length > 0) {
        // Merchant was already created, just refetch
        await fetchMerchant();
        return;
      }

      const defaultName = 'Your Business';
      const defaultIndustry: IndustryType = 'RETAIL';
      const defaultRegion = 'GLOBAL' as const;

      // Create the merchant
      const { data: newMerchant, error: createError } = await epicengageSupabase
        .from('merchants')
        .insert({
          user_id: user.id,
          name: defaultName,
          industry: defaultIndustry,
          region: defaultRegion,
          onboarding_completed: true
        })
        .select()
        .single();

      if (createError) throw createError;

      // Create default settings
      await epicengageSupabase
        .from('merchant_settings')
        .insert({ merchant_id: newMerchant.id });

      // Create sequences
      const sequenceKeys: SequenceKey[] = ['NEW_CUSTOMER_ONBOARDING', 'POST_ACTION_FOLLOWUP', 'INACTIVE_30D'];
      const sequences = sequenceKeys.map(key => ({
        merchant_id: newMerchant.id,
        key,
        enabled: false
      }));

      const { data: createdSequences, error: seqError } = await epicengageSupabase
        .from('sequences')
        .insert(sequences)
        .select();

      if (seqError) console.error('Error creating sequences:', seqError);

      // Auto-generate automation templates
      const templatesMap: Record<string, string> = {};

      for (const sequenceKey of sequenceKeys) {
        const automationConfig = AUTOMATION_TEMPLATES[sequenceKey];
        
        for (const templateDef of automationConfig.templates) {
          const content = generateTemplateContent(
            templateDef.key,
            defaultName,
            defaultIndustry,
            'FRIENDLY'
          );

          const { data: template, error: templateError } = await epicengageSupabase
            .from('email_templates_v2')
            .insert({
              merchant_id: newMerchant.id,
              name: templateDef.name,
              type: 'AUTOMATION',
              editor_type: 'STRUCTURED',
              origin: 'SYSTEM_GENERATED',
              subject: content.subject,
              body_json: { text: content.body }
            })
            .select()
            .single();

          if (templateError) {
            console.error('Error creating template:', templateError);
            continue;
          }

          templatesMap[`${sequenceKey}_${templateDef.key}`] = template.id;
        }
      }

      // Create sequence steps
      for (const sequence of createdSequences || []) {
        const automationConfig = AUTOMATION_TEMPLATES[sequence.key as SequenceKey];
        
        const steps = automationConfig.templates.map((templateDef, index) => ({
          sequence_id: sequence.id,
          step_order: index + 1,
          delay_hours: templateDef.delay_hours,
          template_id: templatesMap[`${sequence.key}_${templateDef.key}`] || null,
          guard_once_per_customer: true
        }));

        const { error: stepsError } = await epicengageSupabase
          .from('sequence_steps')
          .insert(steps);

        if (stepsError) console.error('Error creating sequence steps:', stepsError);
      }

      setMerchant(newMerchant as Merchant);

      // Fetch settings
      const { data: settingsData } = await epicengageSupabase
        .from('merchant_settings')
        .select('*')
        .eq('merchant_id', newMerchant.id)
        .maybeSingle();

      setSettings(settingsData as MerchantSettings | null);
    } catch (err: any) {
      console.error('Error auto-creating merchant:', err);
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchMerchant();
  }, [user]);

  const createMerchant = async (data: { name: string; industry: Merchant['industry']; region: Merchant['region'] }) => {
    if (!user) throw new Error('Must be logged in');

    // 1. Create the merchant with onboarding_completed = true
    const { data: newMerchant, error: createError } = await epicengageSupabase
      .from('merchants')
      .insert({
        user_id: user.id,
        name: data.name,
        industry: data.industry,
        region: data.region,
        onboarding_completed: true
      })
      .select()
      .single();

    if (createError) throw createError;

    // 2. Create default settings
    await epicengageSupabase
      .from('merchant_settings')
      .insert({ merchant_id: newMerchant.id });

    // 3. Create sequences and get their IDs
    const sequenceKeys: SequenceKey[] = ['NEW_CUSTOMER_ONBOARDING', 'POST_ACTION_FOLLOWUP', 'INACTIVE_30D'];
    const sequences = sequenceKeys.map(key => ({
      merchant_id: newMerchant.id,
      key,
      enabled: false
    }));

    const { data: createdSequences, error: seqError } = await epicengageSupabase
      .from('sequences')
      .insert(sequences)
      .select();

    if (seqError) throw seqError;

    // 4. Auto-generate all automation templates
    const templatesMap: Record<string, string> = {}; // templateKey -> templateId

    for (const sequenceKey of sequenceKeys) {
      const automationConfig = AUTOMATION_TEMPLATES[sequenceKey];
      
      for (const templateDef of automationConfig.templates) {
        const content = generateTemplateContent(
          templateDef.key,
          data.name,
          data.industry,
          'FRIENDLY'
        );

        // Create the template
        const { data: template, error: templateError } = await epicengageSupabase
          .from('email_templates_v2')
          .insert({
            merchant_id: newMerchant.id,
            name: templateDef.name,
            type: 'AUTOMATION',
            editor_type: 'STRUCTURED',
            origin: 'SYSTEM_GENERATED',
            subject: content.subject,
            body_json: { text: content.body }
          })
          .select()
          .single();

        if (templateError) {
          console.error('Error creating template:', templateError);
          continue;
        }

        templatesMap[`${sequenceKey}_${templateDef.key}`] = template.id;
      }
    }

    // 5. Create sequence steps and link them to templates
    for (const sequence of createdSequences || []) {
      const automationConfig = AUTOMATION_TEMPLATES[sequence.key as SequenceKey];
      
      const steps = automationConfig.templates.map((templateDef, index) => ({
        sequence_id: sequence.id,
        step_order: index + 1,
        delay_hours: templateDef.delay_hours,
        template_id: templatesMap[`${sequence.key}_${templateDef.key}`] || null,
        guard_once_per_customer: true
      }));

      const { error: stepsError } = await epicengageSupabase
        .from('sequence_steps')
        .insert(steps);

      if (stepsError) {
        console.error('Error creating sequence steps:', stepsError);
      }
    }

    await fetchMerchant();
    return newMerchant as Merchant;
  };

  const updateMerchant = async (data: { name?: string; industry?: Merchant['industry']; region?: Merchant['region'] }) => {
    if (!merchant) throw new Error('No merchant');

    const { error: updateError } = await epicengageSupabase
      .from('merchants')
      .update(data)
      .eq('id', merchant.id);

    if (updateError) throw updateError;
    await fetchMerchant();
  };

  const updateSettings = async (newSettings: Partial<MerchantSettings>) => {
    if (!merchant) throw new Error('No merchant');

    const { error: updateError } = await epicengageSupabase
      .from('merchant_settings')
      .upsert({
        merchant_id: merchant.id,
        ...settings,
        ...newSettings
      });

    if (updateError) throw updateError;
    await fetchMerchant();
  };

  return (
    <MerchantContext.Provider value={{
      merchant,
      settings,
      loading,
      error,
      refetch: fetchMerchant,
      createMerchant,
      updateMerchant,
      updateSettings
    }}>
      {children}
    </MerchantContext.Provider>
  );
}

export function useMerchant() {
  const context = useContext(MerchantContext);
  if (context === undefined) {
    throw new Error('useMerchant must be used within a MerchantProvider');
  }
  return context;
}